import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  motion,
  useInView,
  animate,
  useReducedMotion,
} from "framer-motion";
import "./About.css";
import aboutImage from "../assets/About us.jpg";

const CountUp = ({ end, duration = 1.6, suffix = "+" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  // ✅ More reliable on mobile than negative rootMargin
  const isInView = useInView(ref, { once: true, amount: 0.35 });

  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!isInView) return;

    // If user prefers reduced motion, just jump to end
    if (reduceMotion) {
      setCount(end);
      return;
    }

    // ✅ Framer animate is smoother + less likely to be throttled on mobile
    const controls = animate(0, end, {
      duration,
      ease: "easeOut",
      onUpdate: (latest) => setCount(Math.round(latest)),
    });

    return () => controls.stop();
  }, [end, duration, isInView, reduceMotion]);

  return (
    <span ref={ref} className="countup">
      {count}
      {suffix}
    </span>
  );
};

const About = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);

  // ✅ Disable mouse tracking on touch devices (mobile)
  const isFinePointer = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(pointer: fine)").matches;
  }, []);

  useEffect(() => {
    if (!isFinePointer) return;

    const handleMouseMove = (e) => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        setMousePosition({ x, y });
      }
    };

    const section = sectionRef.current;
    if (section) {
      section.addEventListener("mousemove", handleMouseMove);
      return () => section.removeEventListener("mousemove", handleMouseMove);
    }
  }, [isFinePointer]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const highlightVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const StatTitle = ({ end, suffix, label }) => (
    <h3 className="highlight-title">
      <span className="highlight-number">
        <CountUp end={end} suffix={suffix} />
      </span>
      <span className="highlight-label">{label}</span>
    </h3>
  );

  const sharedHover = {
    scale: 1.08,
    y: -15,
    rotateY: 5,
    transition: { duration: 0.4, type: "spring", stiffness: 300 },
  };

  // ✅ Only apply 3D transform on fine pointers (desktop/laptop)
  const sharedTransformStyle = isFinePointer
    ? {
        transform: `perspective(1000px) translate3d(${mousePosition.x * 2}px, ${
          mousePosition.y * 2
        }px, 0)`,
      }
    : undefined;

  return (
    <section className="about-wrapper" id="about" ref={sectionRef}>
      <motion.div
        className="about-container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <motion.div className="about-left" variants={itemVariants}>
          <img src={aboutImage} alt="About The We Dev" className="about-img" />
        </motion.div>

        <motion.div className="about-right" variants={itemVariants}>
          <motion.div
            className="about-label-wrapper"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.p
              className="about-label"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 8px 25px rgba(26, 121, 193, 0.3)",
              }}
              transition={{ duration: 0.2 }}
            >
              <motion.span
                className="label-icon"
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              >
                ✦
              </motion.span>
              <span className="label-text">WHO WE ARE</span>
              <motion.span
                className="label-line"
                initial={{ width: 0 }}
                whileInView={{ width: "80%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </motion.p>
          </motion.div>

          <motion.h2
            className="about-heading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            We build{" "}
            <motion.span
              className="gradient-text"
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
            >
              impactful
            </motion.span>{" "}
            websites <br className="heading-break" />
            for ambitious brands.
          </motion.h2>

          <motion.p
            className="about-text"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            At <strong>THE WE DEV</strong>, we blend design and development to
            deliver digital experiences that don't just look great, but drive
            real results. We empower brands through clean code, elegant design,
            and powerful performance.
          </motion.p>

          <motion.div
            className="about-highlights"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {/* Card 1 */}
            <motion.div
              className="highlight-box"
              variants={highlightVariants}
              whileHover={isFinePointer ? sharedHover : undefined}
              style={sharedTransformStyle}
            >
              <div className="highlight-icon">
                <div className="icon-bg-glow"></div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>

              <StatTitle end={100} suffix="+" label="Projects" />
              <p>Custom developed websites tailored for performance.</p>

              <div className="highlight-shine"></div>

              <div className="highlight-particles">
                {Array.from({ length: 5 }, (_, i) => (
                  <motion.div
                    key={i}
                    className="highlight-particle"
                    animate={{
                      x: [0, Math.random() * 100 - 50],
                      y: [0, Math.random() * 100 - 50],
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                    }}
                    transition={{
                      duration: 2 + Math.random(),
                      repeat: Infinity,
                      delay: i * 0.3,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </div>

              <div className="highlight-glow-effect"></div>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              className="highlight-box"
              variants={highlightVariants}
              whileHover={isFinePointer ? sharedHover : undefined}
              style={sharedTransformStyle}
            >
              <div className="highlight-icon">
                <div className="icon-bg-glow"></div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 9h6M9 15h6" />
                </svg>
              </div>

              <StatTitle end={5} suffix="+" label="Services" />
              <p>From Web Development to SEO & AI integration.</p>

              <div className="highlight-shine"></div>

              <div className="highlight-particles">
                {Array.from({ length: 5 }, (_, i) => (
                  <motion.div
                    key={i}
                    className="highlight-particle"
                    animate={{
                      x: [0, Math.random() * 100 - 50],
                      y: [0, Math.random() * 100 - 50],
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                    }}
                    transition={{
                      duration: 2 + Math.random(),
                      repeat: Infinity,
                      delay: i * 0.3,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </div>

              <div className="highlight-glow-effect"></div>
            </motion.div>

            {/* Card 3 */}
            <motion.div
              className="highlight-box"
              variants={highlightVariants}
              whileHover={isFinePointer ? sharedHover : undefined}
              style={sharedTransformStyle}
            >
              <div className="highlight-icon">
                <div className="icon-bg-glow"></div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>

              <StatTitle end={100} suffix="%" label="Satisfaction" />
              <p>Trusted by startups & agencies for impactful delivery.</p>

              <div className="highlight-shine"></div>

              <div className="highlight-particles">
                {Array.from({ length: 5 }, (_, i) => (
                  <motion.div
                    key={i}
                    className="highlight-particle"
                    animate={{
                      x: [0, Math.random() * 100 - 50],
                      y: [0, Math.random() * 100 - 50],
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                    }}
                    transition={{
                      duration: 2 + Math.random(),
                      repeat: Infinity,
                      delay: i * 0.3,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </div>

              <div className="highlight-glow-effect"></div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default About;
